import React, {FC, useState, useEffect} from 'react'
import {Modal, Button, Alert} from 'react-bootstrap'
import {externalCalendarApiService, ExternalCalendar, ICalTestResult} from '../services/externalCalendarApi'

interface ManageCalendarsModalProps {
  show: boolean
  onClose: () => void
  onSuccess?: () => void
}

const ManageCalendarsModal: FC<ManageCalendarsModalProps> = ({show, onClose, onSuccess}) => {
  const [calendars, setCalendars] = useState<ExternalCalendar[]>([])
  const [icalUrl, setIcalUrl] = useState('')
  const [calendarName, setCalendarName] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingList, setIsLoadingList] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [securityWarnings, setSecurityWarnings] = useState<string[]>([])
  const [securityScore, setSecurityScore] = useState<number>(100)
  const [showAddForm, setShowAddForm] = useState(false)

  // Carrega lista de calendários ao abrir o modal
  useEffect(() => {
    if (show) {
      loadCalendars()
    }
  }, [show])

  const loadCalendars = async () => {
    setIsLoadingList(true)
    try {
      const data = await externalCalendarApiService.getCalendars()
      setCalendars(data)
    } catch (error) {
      console.error('Erro ao carregar calendários:', error)
    } finally {
      setIsLoadingList(false)
    }
  }

  const handleSave = async () => {
    setError(null)
    
    if (!icalUrl.trim()) {
      setError('URL do calendário é obrigatória')
      return
    }
    
    if (!calendarName.trim()) {
      setError('Nome do calendário é obrigatório')
      return
    }

    setIsLoading(true)
    try {
      // Testa a URL primeiro
      const testResult = await externalCalendarApiService.testIcalUrl(icalUrl.trim())
      
      if (!testResult.valid) {
        setError(testResult.error || 'URL inválida ou inacessível')
        setSecurityWarnings([])
        setSecurityScore(100)
        return
      }

      // Atualiza avisos de segurança
      setSecurityWarnings(testResult.security_warnings || [])
      setSecurityScore(testResult.security_score || 100)

      // Cria o calendário
      await externalCalendarApiService.createCalendar({
        name: calendarName.trim(),
        ical_url: icalUrl.trim(),
        is_active: true
      })

      // Limpa o formulário
      setIcalUrl('')
      setCalendarName('')
      setSecurityWarnings([])
      setSecurityScore(100)
      setShowAddForm(false)
      
      // Recarrega a lista
      await loadCalendars()
      
      // Notifica sucesso
      if (onSuccess) {
        onSuccess()
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Erro ao adicionar calendário')
    } finally {
      setIsLoading(false)
    }
  }

  const handleDelete = async (calendarId: number) => {
    if (!window.confirm('Tem certeza que deseja remover este calendário?')) {
      return
    }

    try {
      await externalCalendarApiService.deleteCalendar(calendarId)
      await loadCalendars()
      
      if (onSuccess) {
        onSuccess()
      }
    } catch (error: any) {
      alert(error.response?.data?.message || 'Erro ao remover calendário')
    }
  }

  const handleToggleActive = async (calendar: ExternalCalendar) => {
    try {
      await externalCalendarApiService.updateCalendar(calendar.id, {
        is_active: !calendar.is_active
      })
      await loadCalendars()
      
      if (onSuccess) {
        onSuccess()
      }
    } catch (error: any) {
      alert(error.response?.data?.message || 'Erro ao atualizar calendário')
    }
  }

  const handleClose = () => {
    setIcalUrl('')
    setCalendarName('')
    setError(null)
    setSecurityWarnings([])
    setSecurityScore(100)
    setShowAddForm(false)
    onClose()
  }

  const getSecurityBadgeColor = (score: number) => {
    if (score >= 80) return 'success'
    if (score >= 50) return 'warning'
    return 'danger'
  }

  const getButtonVariant = (score: number) => {
    if (score >= 80) return 'primary'
    if (score >= 50) return 'warning'
    return 'danger'
  }

  const getButtonText = (score: number) => {
    if (score >= 80) return 'Adicionar Calendário'
    if (score >= 50) return 'Adicionar Mesmo Assim'
    return 'Adicionar (Não Recomendado)'
  }

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Gerenciar Calendários Externos</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Lista de calendários */}
        <div className="mb-5">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">Calendários Adicionados</h5>
            <Button 
              variant="primary" 
              size="sm"
              onClick={() => setShowAddForm(!showAddForm)}
            >
              <i className="bi bi-plus-circle me-2"></i>
              {showAddForm ? 'Cancelar' : 'Adicionar Novo'}
            </Button>
          </div>

          {isLoadingList ? (
            <div className="text-center py-5">
              <span className="spinner-border spinner-border-sm me-2"></span>
              Carregando calendários...
            </div>
          ) : calendars.length === 0 ? (
            <Alert variant="info">
              <i className="bi bi-info-circle me-2"></i>
              Nenhum calendário externo adicionado ainda.
            </Alert>
          ) : (
            <div className="list-group">
              {calendars.map((calendar) => (
                <div 
                  key={calendar.id} 
                  className="calendar-list-item d-flex justify-content-between align-items-center calendar-item-hover"
                >
                  <div className="flex-grow-1">
                    <div className="d-flex align-items-center">
                      <div 
                        className="me-3" 
                        style={{
                          width: '20px',
                          height: '20px',
                          backgroundColor: calendar.color || '#3788d8',
                          borderRadius: '4px'
                        }}
                      ></div>
                      <div>
                        <h6 className="mb-0">{calendar.name}</h6>
                        <small className="text-muted" style={{fontSize: '0.75rem'}}>
                          {calendar.ical_url.length > 60 
                            ? calendar.ical_url.substring(0, 60) + '...' 
                            : calendar.ical_url}
                        </small>
                      </div>
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={calendar.is_active}
                        onChange={() => handleToggleActive(calendar)}
                        title={calendar.is_active ? 'Desativar' : 'Ativar'}
                      />
                    </div>
                    <Button
                      variant="light-danger"
                      size="sm"
                      onClick={() => handleDelete(calendar.id)}
                      title="Remover calendário"
                      className="btn-icon"
                    >
                      <i className="bi bi-trash fs-6"></i>
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Formulário de adicionar */}
        {showAddForm && (
          <>
            <hr />
            <h5 className="mb-4">Adicionar Novo Calendário</h5>
            
            {error && (
              <Alert variant="danger" dismissible onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {securityWarnings.length > 0 && (
              <Alert variant={getSecurityBadgeColor(securityScore)}>
                <div className="d-flex align-items-center mb-2">
                  <i className="bi bi-shield-exclamation fs-4 me-2"></i>
                  <strong>Análise de Segurança (Score: {securityScore}/100)</strong>
                </div>
                <ul className="mb-0">
                  {securityWarnings.map((warning, index) => (
                    <li key={index}>{warning}</li>
                  ))}
                </ul>
              </Alert>
            )}

            <div className="mb-3">
              <label className="form-label required">Nome do Calendário</label>
              <input
                type="text"
                className="form-control"
                placeholder="Ex: Meu Calendário Pessoal"
                value={calendarName}
                onChange={(e) => setCalendarName(e.target.value)}
                disabled={isLoading}
              />
              <div className="form-text">
                Um nome descritivo para identificar este calendário
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label required">URL do Calendário (iCal)</label>
              <input
                type="url"
                className="form-control"
                placeholder="https://calendar.google.com/calendar/ical/..."
                value={icalUrl}
                onChange={(e) => setIcalUrl(e.target.value)}
                disabled={isLoading}
              />
              <div className="form-text">
                Cole aqui a URL no formato iCal (.ics) do seu calendário
              </div>
            </div>

            <Alert variant="info">
              <strong>Como obter a URL do calendário:</strong>
              <ul className="mb-0 mt-2">
                <li><strong>Google Calendar:</strong> Configurações → Configurações do calendário → Endereço secreto no formato iCal</li>
                <li><strong>Apple iCloud:</strong> Calendário → Compartilhar calendário → URL pública</li>
                <li><strong>Outlook/Office 365:</strong> Configurações → Exibir todas as configurações do Outlook → Calendário compartilhado</li>
              </ul>
            </Alert>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={isLoading}>
          Fechar
        </Button>
        {showAddForm && (
          <Button 
            variant={getButtonVariant(securityScore)} 
            onClick={handleSave} 
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                Adicionando...
              </>
            ) : (
              getButtonText(securityScore)
            )}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  )
}

export default ManageCalendarsModal

